/**
 * The notification feed's pure rules: a row's shape, which rows belong to one thread, the bell's unread count and
 * how many rows a header bell draws. No Firebase here, on purpose: the home headers' hook and its tests read these
 * without loading the Firestore client (notificationsRepository re-exports every one of them, so its importers are
 * unchanged).
 */

import { signinLinkPath } from '../services/composio/signinLink';
import { ROUTINE_WAIT_NOTICE_KIND, isRoutineWaitFixPath } from '../types/routineWaits';

export type FirestoreNotification = {
  id: string;
  title: string;
  message: string;
  timestamp: string; // ISO string for compatibility with existing UI
  read: boolean;
  metadata?: Record<string, any>;
  created_at?: any;
};

/**
 * Per-thread notification badges (2026-09-08): a row that belongs to ONE
 * thread — a routine run, a thread message, anything stamped with a
 * thread_id — is counted by that thread's own badge, not by the account-level
 * bell. The bell's unread count is account-level only.
 */
export function isThreadScopedNotification(n: Pick<FirestoreNotification, 'metadata'>): boolean {
  const meta = n.metadata;
  if (!meta || typeof meta !== 'object') return false;
  // A request waiting on THIS person's decision (catalog v18: someone asked to
  // archive a thread they pinned) is account-level: the bell counts it even
  // though it opens a thread, so it is never buried in one thread's badge.
  if (meta.kind === 'archive_request') return false;
  // An update on an issue the person reported, or one that hit them (2026-10-16), is about their account's issue,
  // not one thread's conversation: the bell counts it, though its button opens the thread the report came from.
  if (meta.kind === 'issue_update') return false;
  if (typeof meta.thread_id === 'string' && meta.thread_id.trim()) return true;
  if (meta.type === 'routine_run') return true;
  if (meta.category === 'threadMessage') return true;
  return false;
}

/** The thread a row points at, when it does (id + optional workspace). */
export function notificationThreadLink(
  n: Pick<FirestoreNotification, 'metadata'>,
): { threadId: string; ws: string | null } | null {
  const meta = n.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const threadId = typeof meta.thread_id === 'string' ? meta.thread_id.trim() : '';
  if (!threadId) return null;
  const ws = typeof meta.account_id === 'string' && meta.account_id.trim()
    ? meta.account_id.trim()
    : typeof meta.ws === 'string' && meta.ws.trim() ? meta.ws.trim() : null;
  return { threadId, ws };
}

/**
 * The fix a row opens, when it is a notice that something needs this person: a sign-in in Prest0n's browser that
 * ended (sign in once, S5, 2026-10-04; metadata.kind 'signin_lapse') opens the Integrations page with that sign-in's
 * window; a routine waiting on them (2026-10-17; 'routine_wait') opens the fix its notice names, only an address on
 * the waits' allow-list (routineWaits.isRoutineWaitFixPath). Null otherwise.
 */
export function notificationSigninLink(n: Pick<FirestoreNotification, 'metadata'>): string | null {
  const meta = n.metadata;
  if (!meta || typeof meta !== 'object') return null;
  if (meta.kind === 'signin_lapse') return signinLinkPath(meta.connection_id);
  if (meta.kind === ROUTINE_WAIT_NOTICE_KIND) return isRoutineWaitFixPath(meta.fix_path) ? meta.fix_path : null;
  return null;
}

/** What a fix row's control says, read off the address it opens (notificationSigninLink's). */
export function notificationFixVerb(path: string): string {
  if (path.startsWith('/integrations?connect=')) return 'reconnect';
  if (path === '/integrations?category=legal') return 'open legal apps';
  if (path === '/integrations') return 'open your apps';
  if (path === '/subscription') return 'open billing';
  if (path.startsWith('/?thread=')) return 'open its thread';
  return 'sign in again';
}

/** The rows a header bell's list draws (the app header's and both home headers'). */
export const BELL_ROWS = 10;

/** Account-level unread count — the number the bell shows. */
export function countAccountLevelUnread(notifications: readonly FirestoreNotification[]): number {
  let n = 0;
  for (const item of notifications) {
    if (!item.read && !isThreadScopedNotification(item)) n += 1;
  }
  return n;
}
